import React from 'react';
import { TouchableOpacity, Text, StyleSheet, ActivityIndicator, ViewStyle, TextStyle } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useColors, ThemeColors } from '../../hooks/useColors';
import { spacing } from '../../theme';

interface ButtonProps {
  onPress: () => void;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'default';
  size?: 'sm' | 'md' | 'lg';
  icon?: keyof typeof Feather.glyphMap;
  iconSize?: number;
  loading?: boolean;
  disabled?: boolean;
  style?: ViewStyle;
  textStyle?: TextStyle;
  iconColor?: string;
}

export function Button({
  onPress,
  children,
  variant = 'primary',
  size = 'md',
  icon,
  iconSize,
  loading,
  disabled,
  style,
  textStyle,
  iconColor,
}: ButtonProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  const getVariantStyle = () => {
    switch (variant) {
      case 'secondary':
        return styles.secondary;
      case 'outline':
        return styles.outline;
      case 'ghost':
        return styles.ghost;
      case 'default':
        return styles.default;
      default:
        return styles.primary;
    }
  };

  const getSizeStyle = () => {
    switch (size) {
      case 'sm':
        return styles.small;
      case 'lg':
        return styles.large;
      default:
        return styles.medium;
    }
  };

  const getTextStyle = () => {
    switch (variant) {
      case 'outline':
      case 'ghost':
        return styles.textDark;
      case 'default':
        return styles.textOutline;
      default:
        return styles.textLight;
    }
  };

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={loading || disabled}
      style={[
        styles.button,
        getVariantStyle(),
        getSizeStyle(),
        (loading || disabled) && styles.disabled,
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator color={variant === 'primary' ? colors.background : colors.primary} />
      ) : (
        <>
          {icon && (
            <Feather
              name={icon}
              size={iconSize || (size === 'sm' ? 16 : 20)}
              color={iconColor || (variant === 'outline' ? colors.primary : variant === 'primary' ? colors.background : colors.primary)}
              style={styles.icon}
            />
          )}
          <Text style={[getTextStyle(), textStyle]}>
            {children}
          </Text>
        </>
      )}
    </TouchableOpacity>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
  },
  primary: {
    backgroundColor: colors.primary,
  },
  secondary: {
    backgroundColor: colors.secondary,
  },
  outline: {
    backgroundColor: colors.backgroundSecondary,
    borderWidth: 1,
    borderColor: colors.primary,
  },
  ghost: {
    backgroundColor: colors.backgroundSecondary,
  },
  default: {
    backgroundColor: colors.backgroundSecondary,
  },
  small: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm + 4,
  },
  medium: {
    paddingVertical: spacing.sm + 4,
    paddingHorizontal: spacing.md,
  },
  large: {
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
  },
  disabled: {
    opacity: 0.7,
  },
  textLight: {
    color: colors.background,
    fontSize: 16,
    fontWeight: '600',
  },
  textDark: {
    color: colors.primary,
    fontSize: 16,
    fontWeight: '600',
  },
  textOutline: {
    color: colors.primary,
  },
  icon: {
    marginRight: spacing.sm,
  },
}); 